import React, { useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { AutoCompleteKeyword, Page } from "@/model";
import { useCreateAutocompleteKeyword, useDeleteAutocompleteKeywords } from "@/query/autocompleteKeyword";
import { useAgentId, useDialog, useSelection } from "@/hook";
import SelectionActionBar from "@/component/SelectionActionBar";
import { errorToast, successToast } from "@/component/Toast";
import { IndeterminateCheckbox } from "@/component/input";
import SortButton from "@/component/SortBurtton";

import { AutocompleteKeywordPageParam } from "../AutocompleteKeywordView";
import AutocompleteKeywordFragment from "./AutocompleteKeywordFragment";


interface AutocompleteKeywordTableProps {
    page?: Page<AutoCompleteKeyword>;
    pageParam: AutocompleteKeywordPageParam;
    onClickSortHead: (order: 'asc' | 'desc') => void;
}

const AutocompleteKeywordTable: React.FC<AutocompleteKeywordTableProps> = (props) => {
    const { page, pageParam, onClickSortHead } = props;

    const intl = useIntl();
    const dialog = useDialog();
    const agentId = useAgentId();
    const { autocompleteId } = useParams<{ autocompleteId: string }>();

    const { mutateAsync: deleteKeywords } = useDeleteAutocompleteKeywords(agentId, autocompleteId!);
    
    const pageIds = useMemo(() => page?.content.map(autocompleteKeyword => autocompleteKeyword.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0
    });

    const onCheckOne = (e: React.ChangeEvent<HTMLInputElement>, autocompleteKeyword: AutoCompleteKeyword) => {
        markOne(autocompleteKeyword.id, e.currentTarget.checked);
    };

    const deleteByIds = async (ids: string[]) => {
        await deleteKeywords({ ids })
            .then(() => {
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteKeywords({ keyword: pageParam.searchKeyword })
            .then(() => {
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const onClickDeleteBtn = (e: React.MouseEvent) => {
        e.preventDefault();

        if (selectedIds.length === 0) {
            errorToast(intl.formatMessage({ id: "t007" }));
            return;
        }
        dialog.open({
            title: intl.formatMessage({ id: "i08008" }),
            content: intl.formatMessage({ id: "i08009" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteByIds(selectedIds),
            confirmText: intl.formatMessage({ id: "b0008" }),
            variant: "danger"
        });
    };

    return (
        <div role="table"
             className="selection-table grid-table-autocomplete-keyword"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={markPage}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <SortButton order={pageParam.sortOrder}
                                onClick={() => onClickSortHead(pageParam.sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                        {intl.formatMessage({ id: "i08030" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <AutocompleteKeywordForm />
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((autocompleteKeyword: AutoCompleteKeyword) => (
                    <AutocompleteKeywordFragment key={autocompleteKeyword.id}
                                                 autocompleteKeyword={autocompleteKeyword}
                                                 searchKeyword={pageParam.searchKeyword}
                                                 selectedIds={selectedIds}
                                                 onCheck={onCheckOne}
                                                 onDelete={() => deleteByIds([autocompleteKeyword.id])}
                    />
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <div className="col-span-full">
                <SelectionActionBar centerByParent
                                    selectionCount={selectionCount}
                                    selectAll={() => markAll(true)}
                                    deSelect={() => markAll(false)}
                                    remove={onClickDeleteBtn}
                />
            </div>
        </div>
    );
};

const AutocompleteKeywordForm = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const { autocompleteId } = useParams<{ autocompleteId: string }>();

    const [validated, setValidated] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const {
        mutateAsync: createAutocompleteKeyword,
        isPending: isCreating
    } = useCreateAutocompleteKeyword(agentId, autocompleteId!);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.trim() !== ""){
            setValidated(false);
        }
    };
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const keyword = inputRef.current?.value.trim() || "";
        if (!keyword) {
            setValidated(true);
            return;
        }

        await createAutocompleteKeyword({ keyword })
            .then(() => {
                successToast(intl.formatMessage({ id: "t001" }));
                setValidated(false);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t002" }));
            })
            .finally(() => {
                if (inputRef.current) {
                    inputRef.current.value = "";
                    setTimeout(() => {
                        inputRef.current!.focus();
                    }, 0);
                }
            });
    };

    return (
        <form className="selection-table-form relative w-full py-[9px] pl-[34px] pr-3.5 bg-white"
              onSubmit={onSubmit}
        >
            <input type="text"
                   ref={inputRef}
                   className={classNames(validated && "input-invalid")}
                   placeholder={intl.formatMessage({ id: "i08021" })}
                   disabled={isCreating}
                   onChange={onChange}
            />
        </form>
    );
};

export default AutocompleteKeywordTable;